const PrintDriverRefs = require('@/lib/Printing/PrintDriverRefs');
const PrintJobOptions = require('@/lib/Printing/PrintJobOptions');
const PrintRouting = require('@/lib/Printing/PrintRouting');
const PrintType = require('@/lib/Printing/PrintType');
const { cloneDeep } = require('lodash');
const { kitchenOrderDiff } = require('@/libraries/DigiTickets/Kitchen/kitchenOrderDiff');

/**
 * Used for generating a printing a kitchen receipt *on the till/POS* rather than on the kitchen screen.
 */
class KitchenReceiptPrinter {
    /**
     * @param {CurrentDevice} currentDevice
     * @param {KitchenOrderFactory} kitchenOrderFactory
     * @param {PrintRouter} printRouter
     * @param {ReceiptPrinter} receiptPrinter
     */
    constructor(
        currentDevice,
        kitchenOrderFactory,
        printRouter,
        receiptPrinter,
    ) {
        this.currentDevice = currentDevice;
        this.kitchenOrderFactory = kitchenOrderFactory;
        this.printRouter = printRouter;
        this.receiptPrinter = receiptPrinter;
    }

    /**
     * This method receives an Order creates a KitchenOrder we can then pass to ReceiptPrinter.printKitchenOrder() to
     * print. It is called order from the SellCtrl in ProPoint. When printing a KitchenOrder from the Kitchen App this
     * method is not needed because the Kitchen App is already dealing with KitchenOrders.
     *
     * @param {Order} order
     * @param {?KitchenOrder} [restoredKitchenOrder]
     * @param {boolean} applyFulfillments
     *
     * @return {Promise}
     */
    async printOrder(order, restoredKitchenOrder, applyFulfillments = true) {
        // Convert the Order to a KitchenOrder.
        order.device = this.currentDevice.device;

        let kitchenOrder = await this.kitchenOrderFactory.map(
            order,
            null,
            applyFulfillments,
        );
        if (kitchenOrder.items.length === 0) {
            kitchenOrder.cancelled = true;
        }

        // If a "restoredKitchenOrder" is supplied it means this order came from a cart created by an order edit
        // and that has probably been sent to the kitchen before. We'll do a diff of what was sent to the kitchen then,
        // and what is in the order now. If there is no difference the receipt will not be printed.
        // If no restoredKitchenOrder a receipt will always be printed.
        let diff = null;
        if (restoredKitchenOrder) {
            kitchenOrder.fulfillmentCreatedAt = restoredKitchenOrder.fulfillmentCreatedAt;
            // An edited order keeps its original date, so its date can't tell us when it was modified.
            // This is printed straight after the edit is saved, so use the current time.
            kitchenOrder.modifiedAt = new Date();
            diff = kitchenOrderDiff(restoredKitchenOrder, kitchenOrder);

            // If there was a previous order and there is no difference from it don't print a receipt.
            // If there was no previous order diff would be undefined, not null.
            if (diff === null) {
                return;
            }
        }

        // For each Kitchen Zone make a copy of the order and update the visibility of the items.
        // Then send that order to any printers as required.
        const allKitchenZones = await this.kitchenOrderFactory.getAllKitchenZones();

        // If there are no kitchen zones in the system (the client has not enabled kitchen zones or kitchen
        // functionality (branches.kitchenEnabled)) they should still be able to print kitchen receipts if
        // products.isFood = 1 and devices.autoPrintKitchenReceipts = 1
        if (allKitchenZones.length === 0) {
            this.printKitchenOrder(kitchenOrder, diff);
            return;
        }

        // Print one kitchen receipt for each kitchen zone that this order contains items for.
        kitchenOrder.kitchenZoneIDsInOrder.forEach((zoneID) => {
            const kitchenZone = allKitchenZones.find((zone) => zone.ID === zoneID);
            if (!kitchenZone) {
                console.warn(`Kitchen zone with ID ${zoneID} not found for the currently synced branch. Skipping printing for this zone.`);
                return;
            }

            // Clone the kitchen order so we can modify it for each zone.
            const kitchenOrderForZone = cloneDeep(kitchenOrder);

            // Set the items that should be visible on the receipt for this zone.
            // This will be any item that has this zone in its kitchenZoneIDs.
            // Note that the KitchenOrderFactory (used above) will have already converted null/empty kitchenZoneIDs
            // on items to be an array of all kitchen zones, so we can safely check for zoneID.
            this.kitchenOrderFactory.updateItemVisibilityForZone(kitchenOrderForZone, zoneID);

            // Set the kitchen zone name printed on the receipt to the name of this zone.
            kitchenOrderForZone.receiptZoneName = kitchenZone.name;

            // Print the kitchen receipt for this zone.
            this.printKitchenOrder(kitchenOrderForZone, diff, zoneID);
        });
    }

    /**
     * @param {KitchenOrder} kitchenOrder
     * @param {?{added: KitchenOrderItem[], removed: KitchenOrderItem[]}} [diff]
     * @param {?Number} zoneID
     */
    printKitchenOrder(kitchenOrder, diff, zoneID = null) {
        const printRoutings = this.getPrintRoutingForKitchenZone(zoneID);
        printRoutings.forEach((printRouting) => {
            console.log('Printing kitchen order', kitchenOrder, printRouting);
            const opts = new PrintJobOptions(PrintType.KITCHEN_ORDER);
            opts.printRouting = printRouting;
            this.receiptPrinter.printKitchenOrder(kitchenOrder, diff, opts);
        });
    }

    /**
     * Return the print routing for a kitchen zone.
     * Note that this can return multiple routings to print on multiple printers, which is why it returns an array.
     *
     * @param {number} kitchenZoneID
     *
     * @returns {PrintRouting[]}
     */
    getPrintRoutingForKitchenZone(kitchenZoneID) {
        const defaultRouting = this.printRouter.getRoutingForType(PrintType.KITCHEN_ORDER);

        if (!kitchenZoneID) {
            // Return the default routing for a kitchen order.
            // TODO: Remove this because it's too complicated to have this too.
            // In this case we should just print on the default printer.
            return [defaultRouting];
        }

        // Parse the device's printConfig;
        const devicePrintConfig = this.currentDevice.device.printConfig
            ? JSON.parse(this.currentDevice.device.printConfig)
            : {};

        // If there is no printConfig or no kitchenZones defined, return the default routing.
        if (!devicePrintConfig || !devicePrintConfig.kitchenZones) {
            return [defaultRouting];
        }

        // If this kitchen zone is not defined in the device's printConfig, return the default routing.
        if (!devicePrintConfig.kitchenZones.hasOwnProperty(kitchenZoneID)) {
            return [defaultRouting];
        }

        // At this point we'll have an array of printer names or nulls for this kitchen zone.
        return devicePrintConfig.kitchenZones[kitchenZoneID].map((printerName) => {
            if (!printerName) {
                // If the printerName is null it means print on the default printer.
                return defaultRouting;
            }

            return new PrintRouting(PrintDriverRefs.PRINTER_APP, printerName);
        });
    }
}

module.exports = KitchenReceiptPrinter;
